import { useQueryClient } from 'react-query';
import {
  useDetachUserPaymentMethodMutation,
  useSetDefaultUserPaymentMethodMutation,
  useUpdateUserPaymentMethodMutation
} from 'lib/home/generated/queries';
import { User } from 'lib/home/generated/types/api';
import { merge } from 'lodash';

export interface UseMutationCallbackOptions {
  onMutate?: () => void;
  onError?: () => void;
  onSuccess?: () => void;
}

const QUERY_KEY = ['MyPaymentMethods'];

export const useUpdateMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useUpdateUserPaymentMethodMutation({
    onMutate: async ({ updatePaymentMethodInput }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      const updatedItemIndex = prevData.me.payment_methods.findIndex(
        paymentMethod => paymentMethod.id === updatePaymentMethodInput.id
      );

      const previousDefaultItem = prevData.me.payment_methods.find(paymentMethod => paymentMethod.is_default);

      const updatedPaymentMethods = [...prevData.me.payment_methods];

      // Avoid showing two default payment methods while updating
      if (previousDefaultItem.id !== updatePaymentMethodInput.id) previousDefaultItem.is_default = false;

      updatedPaymentMethods[updatedItemIndex] = merge(
        {},
        updatedPaymentMethods[updatedItemIndex],
        updatePaymentMethodInput
      );

      // Optimistically update the payment methods data
      queryClient.setQueryData(QUERY_KEY, merge({}, prevData, { me: { payment_methods: updatedPaymentMethods } }));

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: async data => {
      // Re-fetch the data after the mutation has settled
      await queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};

export const useDetachMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useDetachUserPaymentMethodMutation({
    onMutate: async ({ paymentMethodId }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      const updatedPaymentMethods = prevData.me.payment_methods.filter(
        paymentMethod => paymentMethod.id !== paymentMethodId
      );

      // Optimistically remove the payment method
      queryClient.setQueryData(
        QUERY_KEY,
        merge({}, prevData, {
          me: {
            payment_methods: updatedPaymentMethods
          }
        })
      );

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: () => {
      // Re-fetch the data after the mutation has settled
      queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};

export const useSetDefaultMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useSetDefaultUserPaymentMethodMutation({
    onMutate: async ({ paymentMethodId }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      if (!prevData?.me?.payment_methods) {
        return { prevData };
      }

      const updatedPaymentMethods = [...prevData.me.payment_methods];
      const oldDefaultItemIndex = updatedPaymentMethods.findIndex(paymentMethod => paymentMethod.is_default);
      const newDefaultItemIndex = updatedPaymentMethods.findIndex(
        paymentMethod => paymentMethod.id === paymentMethodId
      );

      if (updatedPaymentMethods[oldDefaultItemIndex]) updatedPaymentMethods[oldDefaultItemIndex].is_default = false;

      if (updatedPaymentMethods[newDefaultItemIndex]) updatedPaymentMethods[newDefaultItemIndex].is_default = true;

      // Optimistically update the payment methods data
      queryClient.setQueryData(QUERY_KEY, merge({}, prevData, { me: { payment_methods: updatedPaymentMethods } }));

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: () => {
      // Re-fetch the data after the mutation has settled
      queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};
